import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges
} from '@angular/core';
import { BrowserCapabilitiesService, ImageReaderService } from '@classifieds-sell/shared';
import { Photo, SelectedPhotoData } from '@classifieds-sell/trademe-core-ui';
import {
    PHOTO_VALIDATION_ERROR_FILE_SIZE,
    PHOTO_VALIDATION_ERROR_IMAGE_DIMENSIONS,
    PHOTO_VALIDATION_ERROR_IMAGE_LOAD_ERROR,
    PHOTO_VALIDATION_ERROR_IMAGE_SQUARE_DIMENSIONS,
    PHOTO_VALIDATION_ERROR_MIME_TYPE
} from 'libs/trademe-core-ui/src/lib/photo-uploader/constants/photo.constants';
import { getSafeFileNameFromFile, getSafeFileTypeFromFile } from 'libs/trademe-core-ui/src/lib/photo-uploader/helpers/image-metadata';
import { ValidatedPhoto } from 'libs/trademe-core-ui/src/lib/photo-uploader/models/validated-photo';
import { concatMap, delay, filter, from, map, Observable } from 'rxjs';

@Component({
    selector: 'property-agent-profile-photo',
    templateUrl: './property-agent-profile-photo.component.html',
    styleUrls: ['./property-agent-profile-photo.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyAgentProfilePhotoComponent implements OnInit, OnChanges {
    @Input() photo!: Photo;
    @Input() isPhotoRemovalDisabled: boolean;
    isFileReaderApiAvailable: boolean;
    @Output() agentPhotoUploading: EventEmitter<void> = new EventEmitter<void>();
    @Output() agentPhotoUploaded: EventEmitter<void> = new EventEmitter<void>();
    @Output() photoSelected = new EventEmitter<SelectedPhotoData>();
    @Output() agentPhotoRemoved: EventEmitter<Photo> = new EventEmitter<Photo>();
    isInvalidPhoto = false;
    invalidFilesErrorMessages: Map<string, string[]> = new Map<
        string,
        string[]
    >([]);

    constructor(
        private _imageReaderService: ImageReaderService,
        private _browserCapabilitiesService: BrowserCapabilitiesService,
        private _changeDetectorRef: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        this.isFileReaderApiAvailable = this._browserCapabilitiesService.supportsFileReaderApi();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.photo) {
            this.photo = {...this.photo, loading: changes?.photo?.currentValue?.plusSize === null};
        }
    }

    public onAgentPhotoUploading(): void {
        this.isInvalidPhoto = false;
        this.invalidFilesErrorMessages.clear();
        this.agentPhotoUploading.emit();
    }

    public onAgentPhotoUploaded(validatedPhotos: ValidatedPhoto[]): void {
        this.isInvalidPhoto = validatedPhotos.some(
            (f) => f.hasValidationError
        );

        if (this.isInvalidPhoto) {
            this.constructInvalidFileErrorMessage(validatedPhotos);
        }

        from(validatedPhotos)
            .pipe(
                filter((vp) => !vp.hasValidationError),
                delay(0),
                concatMap((vp: ValidatedPhoto) =>
                    this.getFileData(vp.nativeFile)
                )
            )
            .subscribe((s) => {
                this.photoSelected.emit(s);
            });

        this.agentPhotoUploaded.emit();
    }

    private constructInvalidFileErrorMessage(
        validatedPhotos: ValidatedPhoto[]
    ): void {
        validatedPhotos.forEach((f) => {
            if (f.hasValidationError) {
                if (this.invalidFilesErrorMessages.has(f.validationError.key)) {
                    this.invalidFilesErrorMessages
                        .get(f.validationError.key)
                        .push(f.nativeFile.name);
                } else {
                    this.invalidFilesErrorMessages.set(f.validationError.key, [
                        f.nativeFile.name
                    ]);
                }
            }
        });
    }

    private getFileData(file: File): Observable<SelectedPhotoData> {
        return this._imageReaderService.readImageDataFromFileAsync(file).pipe(
            map((image) => {
                return {
                    fileName: getSafeFileNameFromFile(file),
                    fileType: getSafeFileTypeFromFile(file),
                    photoData: image.data
                } as SelectedPhotoData;
            })
        );
    }

    public removeProfileImage(): void {
        if (this.photo) {
            this.agentPhotoRemoved.emit(this.photo);
        }
    }

    public getErrorDetail(key: string) {
        switch (key) {
            case PHOTO_VALIDATION_ERROR_MIME_TYPE:
                return ' because the type of photo is not supported (supported types: PNGs, JPGs, BMPs, or GIFs)';
            case PHOTO_VALIDATION_ERROR_FILE_SIZE:
                return ' because photo size must be smaller than 5MB';
            case PHOTO_VALIDATION_ERROR_IMAGE_DIMENSIONS:
                return ' because photo must be at least 160 pixels tall and wide';
            case PHOTO_VALIDATION_ERROR_IMAGE_LOAD_ERROR:
                return ' because there is a problem with loading the photo';
            case PHOTO_VALIDATION_ERROR_IMAGE_SQUARE_DIMENSIONS:
                return ' because the photo is not a square (1:1 ratio). Please crop your image';
        }
        return '';
    }
}
